import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "../../shared utils/dayjsConfig.js";
import {
  buildSaveBedroomAssignments,
  createBedroom,
  deleteBedroom,
  exportBedroomFireList,
  exportBedroomPdf,
  fetchBedroomResidents,
  fetchBedrooms,
  fetchBedroomsForYear,
  updateBedroom,
  bulkUpdateBedrooms,
  saveRoomSchedule,
} from "../api/bedroomAllocationApi";
import { generateRoomSignsPDF } from "../utils/roomSignsPdf";
import {
  buildPendingChanges,
  filterResidents,
  getAssignedRoom,
  getAssignedRoomHouse,
  getAssignedRoomName,
  getAssignedRoomEnsuite,
  getAvailableRoomNames,
  getRoomById,
  moveResidentInRooms,
} from "../utils/bedroomSelectors";
import {
  validateBedroomAssignmentByName,
  validateBedroomMove,
} from "../utils/bedroomValidation";
import {
  buildPlacementWarnings,
  buildRemovalWarnings,
  buildCoverageGap,
  buildRoomStayAvailability,
  computeClampWindow,
} from "../utils/bedroomStayWarnings";
import useCampuses from "../../Timetable/hooks/useCampuses.js";

const TARGET_DATE = dayjs("2025-06-29");

export default function useBedroomAllocation(selectedYear) {
  const today = dayjs();
  const initialDate = today.isAfter(TARGET_DATE) ? today : TARGET_DATE;

  // In-flight guard for the board save: blocks the repeat-click "retry storm"
  // (ref for the synchronous re-entry check, state to drive the button's UI).
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedCampus, setSelectedCampus] = useState("");
  const { campuses: campusOptions = [] } = useCampuses();
  const [selectedHouse, setSelectedHouse] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedNationality, setSelectedNationality] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  const [roomNameSearch, setRoomNameSearch] = useState("");
  const [roomFilterMode, setRoomFilterMode] = useState("all");
  const [sortField, setSortField] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [addRoomDialogOpen, setAddRoomDialogOpen] = useState(false);
  const [roomEdits, setRoomEdits] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [residents, setResidents] = useState([]);
  const [databaseState, setDatabaseState] = useState({ rooms: [], residents: [] });
  const [displayState, setDisplayState] = useState({ rooms: [], residents: [] });
  const [genders, setGenders] = useState([]);
  const [ages, setAges] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [managementRooms, setManagementRooms] = useState([]);
  // Mid-stay placement/removal advisories, keyed by resident board id, shown in
  // the board's persistent warnings panel.
  const [warningsByResident, setWarningsByResident] = useState({});

  // Open "give them this room for the whole stay?" offer after a mid-stay
  // placement, plus the set of board ids the user opted to allocate whole-stay
  // (their save starts at stay_start instead of the viewed date).
  const [wholeStayPrompt, setWholeStayPrompt] = useState(null);
  const [wholeStayIds, setWholeStayIds] = useState(() => new Set());

  // Over-capacity save rejection (backend 409, code CAPACITY_CONFLICT). Holds
  // the resolution offer shown to the operator: assign the room for as long as
  // it's free, or revert. Null when closed. `clampedEndDatesRef` carries the
  // per-resident end-date overrides into the save — a mutated ref (not state) so
  // a re-save triggered right after "assign" reads the fresh value without a
  // render round-trip.
  const [capacityConflict, setCapacityConflict] = useState(null);
  const clampedEndDatesRef = useRef({});

  const formattedDate = selectedDate.format("YYYY-MM-DD");

  const selectedCampusOption = useMemo(
    () => campusOptions.find((c) => String(c.id) === String(selectedCampus)) || null,
    [campusOptions, selectedCampus]
  );

  const selectedCampusName = selectedCampusOption?.name || "";

  const showToast = useCallback((message, severity = "success", duration) => {
    setToast({ open: true, message, severity, duration });
  }, []);

  const closeToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  const fetchManagementRooms = useCallback(async () => {
    if (!selectedYear) {
      setManagementRooms([]);
      return;
    }

    try {
      const loadedRooms = await fetchBedroomsForYear({ year: selectedYear });
      setManagementRooms(loadedRooms || []);
    } catch (error) {
      console.error(error);
      showToast("Failed to load room management data", "error");
      setManagementRooms([]);
    }
  }, [selectedYear, showToast]);

  const fetchResidents = useCallback(async () => {
    setLoading(true);
    try {
      const {
        residents: loadedResidents,
        genders,
        ages,
        nationalities,
      } = await fetchBedroomResidents({
        date: formattedDate,
        campus: selectedCampusName,
      });

      setResidents(loadedResidents);
      setDatabaseState({ rooms: [], residents: loadedResidents });
      setDisplayState({ rooms: [], residents: loadedResidents });
      setGenders(genders || []);
      setAges(ages || []);
      setNationalities(nationalities || []);
      setValidationErrors({});
      setRoomEdits({});
    } catch (error) {
      console.error(error);
      showToast("Failed to load residents", "error");
      setResidents([]);
      setDatabaseState({ rooms: [], residents: [] });
      setDisplayState({ rooms: [], residents: [] });
    } finally {
      setLoading(false);
    }
  }, [formattedDate, selectedCampusName, showToast]);

  // Lightweight reload of just the resident feed after a save, so the standing
  // gaps audit (which reads the saved bedroomAllocations) re-checks without
  // waiting for a date change. Deliberately does NOT flip `loading` or clear
  // the board — the board is already correct from the save response; updating
  // databaseState.residents lets fetchRooms refresh it in place if needed.
  const refreshResidentsAfterSave = useCallback(async () => {
    try {
      const {
        residents: loadedResidents,
        genders: loadedGenders,
        ages: loadedAges,
        nationalities: loadedNationalities,
      } = await fetchBedroomResidents({
        date: formattedDate,
        campus: selectedCampusName,
      });

      setResidents(loadedResidents);
      setGenders(loadedGenders || []);
      setAges(loadedAges || []);
      setNationalities(loadedNationalities || []);
      setDatabaseState((prev) => ({ ...prev, residents: loadedResidents }));
      setDisplayState((prev) => ({ ...prev, residents: loadedResidents }));
    } catch (error) {
      // Non-fatal — the audit will refresh on the next date navigation.
      console.error(error);
    }
  }, [formattedDate, selectedCampusName]);

  const fetchRooms = useCallback(async () => {
    if (!selectedCampusName) {
      setDatabaseState((prev) => ({ ...prev, rooms: [] }));
      setDisplayState((prev) => ({ ...prev, rooms: [] }));
      return;
    }

    try {
      const groupsWithResidents = await fetchBedrooms({
        date: formattedDate,
        campus: selectedCampusName,
        residents: databaseState.residents,
      });

      setDatabaseState((prev) => ({ ...prev, rooms: groupsWithResidents }));
      setDisplayState((prev) => ({ ...prev, rooms: groupsWithResidents }));
      setValidationErrors({});
      setRoomEdits({});
    } catch (error) {
      console.error(error);
      showToast("Failed to load rooms", "error");
    }
  }, [databaseState.residents, formattedDate, selectedCampusName, showToast]);

  useEffect(() => {
    fetchResidents();
  }, [fetchResidents]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  useEffect(() => {
    setSelectedHouse("");
  }, [selectedCampus]);

  // The board reloads on date/campus change, so old advisories no longer apply.
  useEffect(() => {
    setWarningsByResident({});
  }, [formattedDate, selectedCampusName]);

  useEffect(() => {
    fetchManagementRooms();
  }, [fetchManagementRooms]);

  const filteredResidents = useMemo(
    () => {
      // The allocator is per-campus: rooms are only loaded for the selected
      // campus, so without one we can't tell who is genuinely unassigned and
      // would otherwise dump every resident on every site into the unassigned
      // pool. Show no residents until a campus is chosen, and always scope the
      // pool to that campus.
      if (!selectedCampusName) return [];
      return filterResidents({
        residents,
        selectedCampus: selectedCampusName,
        selectedGender,
        selectedAge,
        selectedNationality,
        nameSearch,
      });
    },
    [
      residents,
      selectedCampusName,
      selectedGender,
      selectedAge,
      selectedNationality,
      nameSearch,
    ]
  );

  const pendingChanges = useMemo(
    () =>
      buildPendingChanges({
        displayRooms: displayState.rooms,
        databaseRooms: databaseState.rooms,
      }),
    [displayState.rooms, databaseState.rooms]
  );

  const hasUnsavedChanges = pendingChanges.length > 0;

  // Gentle "time to save" nudge on a busy board — a toast each time the unsaved
  // count reaches a new multiple of 5. Resets after a save (count back to 0), so
  // it fires again on the next batch. (Autosave-on-move stays parked.)
  const lastNudgedThreshold = useRef(0);
  useEffect(() => {
    const count = pendingChanges.length;
    const threshold = Math.floor(count / 5) * 5;
    if (threshold >= 5 && threshold > lastNudgedThreshold.current) {
      // Longer-lived than a normal toast — it often fires alongside the
      // mid-stay dialog, so give the user time to notice it.
      showToast(`${count} unsaved changes — time to save.`, "info", 12000);
    }
    lastNudgedThreshold.current = threshold;
  }, [pendingChanges.length, showToast]);

  const placementWarnings = useMemo(
    () => Object.values(warningsByResident),
    [warningsByResident]
  );

  // bedroomId -> house, from the year's room list (all campuses), so warnings
  // can show "McNabb east 3" — the allocation feed only carries the room name.
  const roomHouseById = useMemo(() => {
    const map = new Map();
    (managementRooms || []).forEach((room) => {
      if (room && room.id != null) map.set(String(room.id), room.house || "");
    });
    return map;
  }, [managementRooms]);

  // Standing per-date audit: residents on this campus/date who are part-
  // allocated (have a room but a gap somewhere in their stay). Derived from the
  // saved allocations that ride with the resident feed, so it recomputes every
  // time the date's residents reload. Independent of the cosmetic gender/age/
  // name filters — a coverage gap is a real problem regardless of filtering.
  const coverageGaps = useMemo(() => {
    if (!selectedCampusName) return [];
    return residents
      .map((resident) => {
        const message = buildCoverageGap(resident, roomHouseById);
        if (!message) return null;
        return {
          residentId: String(resident.id),
          name: `${resident.name} ${resident.surname}`.trim() || "This resident",
          messages: [message],
        };
      })
      .filter(Boolean);
  }, [residents, selectedCampusName, roomHouseById]);

  const availableRoomNames = useMemo(
    () => getAvailableRoomNames(displayState.rooms),
    [displayState.rooms]
  );

  const houses = useMemo(() => {
    return [
      ...new Set(
        (displayState.rooms || [])
          .map((room) => String(room.house || "").trim())
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [displayState.rooms]);

  const getResidentById = useCallback(
    (residentId) => {
      return residents.find(
        (resident) => String(resident.id) === String(residentId)
      ) || null;
    },
    [residents]
  );

  const getCurrentRoomForResident = useCallback(
    (residentId) => getAssignedRoom(displayState.rooms, residentId),
    [displayState.rooms]
  );

  const formatHouseRoom = useCallback((room) => {
    if (!room) return "";

    const house = String(room.house || "").trim();
    const roomName = String(
      room.bedroomName || room.roomName || room.groupName || ""
    ).trim();

    if (house && roomName) {
      return `${house} ${roomName}`;
    }

    return roomName || house || "";
  }, []);

  const setResidentWarnings = useCallback((resident, messages, previousRoomId) => {
    setWarningsByResident((prev) => {
      const key = String(resident.id);
      const next = { ...prev };
      if (messages && messages.length) {
        next[key] = {
          residentId: key,
          name: `${resident.name} ${resident.surname}`.trim() || "This resident",
          messages,
          // Room they were in before this change, so Undo can put it back
          // (null = they came from / go back to Unassigned).
          previousRoomId: previousRoomId ?? null,
        };
      } else {
        // Re-evaluating with no issues clears any earlier warning for them.
        delete next[key];
      }
      return next;
    });
  }, []);

  const dismissResidentWarning = useCallback((residentId) => {
    setWarningsByResident((prev) => {
      const next = { ...prev };
      delete next[String(residentId)];
      return next;
    });
  }, []);

  const clearAllWarnings = useCallback(() => setWarningsByResident({}), []);

  // Advisory-only check. Moving someone mid-stay (into OR out of a room) is
  // valid but unusual, so we never block it — we surface what the single-night
  // board can't show: nights of the stay this change leaves without a room,
  // and (when the resident carries cross-night allocation data) rooms they
  // already hold on other nights. Results land in the persistent warnings
  // panel. Synchronous — the allocation data rides along with the resident.
  const evaluatePlacement = useCallback(
    (resident, targetRoom, fromRoom) => {
      if (!resident) return;

      const messages = targetRoom
        ? buildPlacementWarnings({
            resident,
            selectedDate,
            targetRoom,
            fromRoom,
            houseById: roomHouseById,
          })
        : buildRemovalWarnings({ resident, selectedDate, fromRoom });

      setResidentWarnings(resident, messages, fromRoom?.id ?? null);
    },
    [selectedDate, setResidentWarnings, roomHouseById]
  );

  const moveResident = useCallback(
    (residentId, targetRoomId, options = {}) => {
      const { prompt = true } = options;
      const resident = getResidentById(residentId);
      if (!resident) return { ok: false, reason: "Resident not found." };

      const currentRoom = getCurrentRoomForResident(residentId);
      const targetRoom = targetRoomId
        ? getRoomById(displayState.rooms, targetRoomId)
        : null;

      const validation = validateBedroomMove({
        resident,
        targetRoom,
        currentRoomId: currentRoom?.id ?? null,
      });

      if (!validation.valid) {
        showToast(validation.reason, "warning");
        setValidationErrors((prev) => ({
          ...prev,
          [residentId]: validation.reason,
        }));
        return { ok: false, reason: validation.reason };
      }

      setDisplayState((prev) => ({
        ...prev,
        rooms: moveResidentInRooms({
          rooms: prev.rooms,
          resident,
          fromRoomId: currentRoom?.id ?? null,
          toRoomId: targetRoom?.id ?? null,
        }),
      }));

      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[residentId];
        return next;
      });

      setRoomEdits((prev) => ({
        ...prev,
        [residentId]: formatHouseRoom(targetRoom),
      }));

      // Re-evaluate advisories for this resident — covers both placing into a
      // room and clearing to Unassigned (targetRoom === null). currentRoom is
      // the room they're leaving (captured before the move above).
      evaluatePlacement(resident, targetRoom, currentRoom);

      // Any earlier whole-stay choice for this person is void — this move
      // re-decides it.
      setWholeStayIds((prev) => {
        if (!prev.has(residentId)) return prev;
        const next = new Set(prev);
        next.delete(residentId);
        return next;
      });

      // Mid-stay placement into a DIFFERENT room (or from Unassigned), on a
      // date after their stay start: offer to give them this room for the whole
      // stay instead of splitting. Skip same-room drops (a no-op).
      const changedRoom =
        targetRoom && String(currentRoom?.id ?? "") !== String(targetRoom.id);
      if (prompt && changedRoom) {
        const start = resident.stay_start ? dayjs(resident.stay_start) : null;
        const midStay =
          start && start.isValid() && dayjs(selectedDate).isAfter(start, "day");
        if (midStay) {
          setWholeStayPrompt({
            resident,
            targetRoom,
            fromRoom: currentRoom,
            selectedDate,
            availability: buildRoomStayAvailability({
              resident,
              targetRoom,
              allResidents: residents,
            }),
          });
        }
      }

      return { ok: true };
    },
    [
      displayState.rooms,
      formatHouseRoom,
      getCurrentRoomForResident,
      getResidentById,
      showToast,
      evaluatePlacement,
      selectedDate,
      residents,
    ]
  );

  // Whole-stay offer actions. Confirm records the intent (save starts at
  // stay_start); keep leaves it as a from-the-viewed-date move; cancel reverts
  // the move entirely (back to the room they came from, or Unassigned).
  const confirmWholeStay = useCallback(() => {
    if (!wholeStayPrompt) return;
    setWholeStayIds((prev) => new Set(prev).add(wholeStayPrompt.resident.id));
    // Whole-stay now covers the earlier nights the placement advisory was
    // flagging (uncovered start / "earlier nights stay in old room"), so that
    // warning is moot — clear it.
    dismissResidentWarning(wholeStayPrompt.resident.id);
    setWholeStayPrompt(null);
  }, [wholeStayPrompt, dismissResidentWarning]);

  const keepFromViewedDate = useCallback(() => setWholeStayPrompt(null), []);

  const cancelWholeStayMove = useCallback(() => {
    if (!wholeStayPrompt) return;
    moveResident(wholeStayPrompt.resident.id, wholeStayPrompt.fromRoom?.id ?? null, {
      prompt: false,
    });
    setWholeStayPrompt(null);
  }, [wholeStayPrompt, moveResident]);

  // Revert the change that raised a warning: put the resident back in the room
  // they were in before it (null = Unassigned), then clear the warning so it
  // goes away even if the reverted state would itself re-warn.
  const undoResidentWarning = useCallback(
    (residentId) => {
      const entry = warningsByResident[String(residentId)];
      if (!entry) return;
      moveResident(residentId, entry.previousRoomId ?? null, { prompt: false });
      dismissResidentWarning(residentId);
    },
    [warningsByResident, moveResident, dismissResidentWarning]
  );

  const assignResidentByRoomName = useCallback(
    (residentId, roomName) => {
      const resident = getResidentById(residentId);
      if (!resident) return { ok: false, reason: "Resident not found." };

      const currentRoom = getCurrentRoomForResident(residentId);
      const validation = validateBedroomAssignmentByName({
        resident,
        roomName,
        rooms: displayState.rooms,
        currentRoomId: currentRoom?.id ?? null,
      });

      setRoomEdits((prev) => ({
        ...prev,
        [residentId]: roomName,
      }));

      if (!validation.valid) {
        setValidationErrors((prev) => ({
          ...prev,
          [residentId]: validation.reason,
        }));
        return { ok: false, reason: validation.reason };
      }

      if (!String(roomName || "").trim()) {
        return moveResident(residentId, null);
      }

      return moveResident(residentId, validation.room.id);
    },
    [displayState.rooms, getCurrentRoomForResident, getResidentById, moveResident]
  );

  const validateAllPendingChanges = useCallback(() => {
    const errors = [];

    pendingChanges.forEach((change) => {
      if (!change.toRoomId) return;

      const resident = getResidentById(change.residentId);
      const room = getRoomById(displayState.rooms, change.toRoomId);
      const result = validateBedroomMove({
        resident,
        targetRoom: room,
        currentRoomId: change.fromRoomId,
      });

      if (!result.valid) {
        errors.push(`${resident?.name || "Resident"}: ${result.reason}`);
      }
    });

    return errors;
  }, [displayState.rooms, getResidentById, pendingChanges]);

  const saveChanges = useMemo(
    () =>
      buildSaveBedroomAssignments({
        displayState,
        databaseState,
        fetchResidents,
        fetchGroups: fetchRooms,
        showToast,
        filteredResidents,
        selectedDate,
        selectedCampus: selectedCampusName,
        setChanges: () => {},
        setDatabaseState,
        setDisplayState,
        wholeStayResidentIds: wholeStayIds,
        clampedEndDates: clampedEndDatesRef.current,
      }),
    [
      databaseState,
      displayState,
      fetchResidents,
      fetchRooms,
      filteredResidents,
      selectedDate,
      selectedCampusName,
      showToast,
      wholeStayIds,
    ]
  );

  // Turn a backend CAPACITY_CONFLICT 409 into the resolution offer. Resolves the
  // rejected resident, works out the whole-stay-aware start and how far the room
  // is free (day before firstUnavailableDate), and records where they came from
  // so "revert" can put them back.
  const openCapacityConflict = useCallback(
    (conflict) => {
      if (!conflict) return;
      const boardId = `${String(conflict.residentType || "").toLowerCase()}-${
        conflict.residentId
      }`;
      const resident = getResidentById(boardId);
      if (!resident) {
        // No matching resident on the current feed — fall back to the message.
        showToast(conflict.message || "Room is over capacity.", "warning");
        return;
      }
      const intendedStart = wholeStayIds.has(resident.id)
        ? resident.stay_start
        : formattedDate;
      const { freeThrough, canClamp } = computeClampWindow(
        conflict.firstUnavailableDate,
        intendedStart
      );
      const change = pendingChanges.find(
        (c) => String(c.residentId) === String(resident.id)
      );
      setCapacityConflict({
        message: conflict.message,
        firstUnavailableDate: conflict.firstUnavailableDate,
        room: {
          id: conflict.bedroomId,
          bedroomName: conflict.bedroomName,
          house: conflict.house,
          capacity: conflict.capacity,
        },
        resident,
        intendedStart,
        freeThrough,
        canClamp,
        fromRoomId: change ? change.fromRoomId : null,
      });
    },
    [getResidentById, wholeStayIds, formattedDate, pendingChanges, showToast]
  );

  const persistChanges = useCallback(
    async (silent = false) => {
      // Ignore repeat clicks while a save is already in flight — the save
      // re-syncs from the server response, so hammering it only piled up
      // duplicate transactions.
      if (isSavingRef.current) return;

      const errors = validateAllPendingChanges();

      if (errors.length > 0) {
        if (!silent) {
          showToast(errors[0], "warning");
        }
        throw new Error(errors[0]);
      }

      isSavingRef.current = true;
      setIsSaving(true);
      try {
        await saveChanges(silent);
        // Saved state is now the source of truth — drop stale advisories, the
        // consumed whole-stay choices and clamp overrides, and reload the
        // resident feed so the gaps audit re-checks immediately.
        setWarningsByResident({});
        setWholeStayIds(new Set());
        // Clear clamp overrides in place (keep the ref's object identity, which
        // the save closure reads live).
        Object.keys(clampedEndDatesRef.current).forEach(
          (k) => delete clampedEndDatesRef.current[k]
        );
        await refreshResidentsAfterSave();
      } catch (err) {
        // Over-capacity rejection: open the resolution dialog instead of failing
        // the save outright. Return a sentinel so callers (date-nav auto-save)
        // stay put rather than navigating away on a "successful" save.
        if (err && err.code === "CAPACITY_CONFLICT") {
          openCapacityConflict(err.conflict);
          return { conflict: true };
        }
        // Stale board: someone else moved a person since this board was loaded.
        // Nothing was applied. Reload from the server (which also drops the
        // pending edits — they were made against a board that no longer exists)
        // and say why. Same sentinel treatment: callers stay on this date so
        // the operator sees the message and the current allocation.
        if (err && err.code === "STALE_BOARD") {
          setWarningsByResident({});
          setWholeStayIds(new Set());
          Object.keys(clampedEndDatesRef.current).forEach(
            (k) => delete clampedEndDatesRef.current[k]
          );
          await fetchRooms();
          await refreshResidentsAfterSave();
          // Always shown, silent or not: the operator's edits are gone.
          showToast(err.message, "warning");
          return { conflict: true, stale: true };
        }
        throw err;
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [
      saveChanges,
      showToast,
      validateAllPendingChanges,
      refreshResidentsAfterSave,
      openCapacityConflict,
      fetchRooms,
    ]
  );

  // "Assign the room for as long as it's free": record the clamped end date and
  // re-save. persistChanges reads the clamp ref live, so the fresh value is used
  // without waiting for a render; it will re-open this dialog if another room in
  // the batch also conflicts.
  const confirmCapacityClamp = useCallback(() => {
    if (!capacityConflict || !capacityConflict.canClamp) return;
    clampedEndDatesRef.current[capacityConflict.resident.id] =
      capacityConflict.freeThrough;
    setCapacityConflict(null);
    persistChanges(false);
  }, [capacityConflict, persistChanges]);

  // "Revert": undo the offending placement back to where they came from (or
  // Unassigned) and drop any clamp for them. moveResident also clears whole-stay
  // intent. The rest of the board's unsaved edits are left for the operator to
  // save when ready.
  const revertCapacityConflict = useCallback(() => {
    if (!capacityConflict) return;
    const { resident, fromRoomId } = capacityConflict;
    delete clampedEndDatesRef.current[resident.id];
    moveResident(resident.id, fromRoomId ?? null, { prompt: false });
    setCapacityConflict(null);
  }, [capacityConflict, moveResident]);

  const dismissCapacityConflict = useCallback(() => setCapacityConflict(null), []);

  const changeDate = useCallback(
    async (increment) => {
      try {
        if (hasUnsavedChanges) {
          const res = await persistChanges(true);
          if (res?.conflict) return; // dialog is open — stay on this date
        }
        setSelectedDate((prevDate) => dayjs(prevDate).add(increment, "day"));
      } catch (error) {
        showToast("Failed to save changes before date change.", "error");
      }
    },
    [hasUnsavedChanges, persistChanges, showToast]
  );

  const setDateWithAutoSave = useCallback(
    async (nextDate) => {
      try {
        if (hasUnsavedChanges) {
          const res = await persistChanges(true);
          if (res?.conflict) return; // dialog is open — stay on this date
        }
        setSelectedDate(dayjs(nextDate));
      } catch (error) {
        showToast("Failed to save changes before date change.", "error");
      }
    },
    [hasUnsavedChanges, persistChanges, showToast]
  );

  // Raw date setters that do NOT auto-save — the module guards navigation with a
  // Save / Discard / Cancel prompt instead.
  const goToDate = useCallback((nextDate) => {
    setSelectedDate(dayjs(nextDate));
  }, []);

  const shiftDate = useCallback((increment) => {
    setSelectedDate((prevDate) => dayjs(prevDate).add(increment, "day"));
  }, []);

  const downloadFireList = useCallback(async () => {
    if (hasUnsavedChanges) {
      await persistChanges(true);
    }
    await exportBedroomFireList({ displayState, formattedDate, showToast });
  }, [displayState, formattedDate, hasUnsavedChanges, persistChanges, showToast]);

  const downloadRoomPdf = useCallback(async () => {
    if (hasUnsavedChanges) {
      await persistChanges(true);
    }
    await exportBedroomPdf({
      displayState,
      formattedDate,
      selectedCampus: selectedCampusName,
      showToast,
    });
  }, [
    displayState,
    formattedDate,
    hasUnsavedChanges,
    persistChanges,
    selectedCampusName,
    showToast,
  ]);

  const downloadRoomSigns = useCallback(async () => {
    if (hasUnsavedChanges) {
      await persistChanges(true);
    }
    generateRoomSignsPDF(
      displayState,
      formattedDate,
      selectedCampusName,
      showToast
    );
  }, [
    displayState,
    formattedDate,
    hasUnsavedChanges,
    persistChanges,
    selectedCampusName,
    showToast,
  ]);

  const handleAddRoom = useCallback(async (bedroomData) => {
    try {
      await createBedroom(bedroomData);
      showToast("Room created and scheduled successfully", "success");
      await fetchResidents();
      await fetchRooms();
      await fetchManagementRooms();
    } catch (error) {
      console.error(error);
      showToast(error.message || "Failed to create room", "error");
    }
  }, [fetchResidents, fetchRooms, fetchManagementRooms, showToast]);

  const handleUpdateRoom = useCallback(async (bedroomData) => {
    try {
      await updateBedroom(bedroomData);
      showToast("Room updated successfully", "success");
      await fetchResidents();
      await fetchRooms();
      await fetchManagementRooms();
    } catch (error) {
      console.error(error);
      showToast(error.message || "Failed to update room", "error");
    }
  }, [fetchResidents, fetchRooms, fetchManagementRooms, showToast]);

  const handleDeleteRoom = useCallback(async (roomId) => {
    try {
      await deleteBedroom(roomId);
      showToast("Room deleted or unscheduled successfully", "success");
      await fetchResidents();
      await fetchRooms();
      await fetchManagementRooms();
    } catch (error) {
      console.error(error);
      showToast(error.message || "Failed to delete room", "error");
    }
  }, [fetchResidents, fetchRooms, fetchManagementRooms, showToast]);

  const getDraftRoomName = useCallback(
    (residentId) => {
      if (Object.prototype.hasOwnProperty.call(roomEdits, residentId)) {
        return roomEdits[residentId];
      }

      const room = getAssignedRoom(displayState.rooms, residentId);
      return formatHouseRoom(room);
    },
    [displayState.rooms, roomEdits, formatHouseRoom]
  );

  const clearRoomAssignment = useCallback(
    (residentId) => {
      setRoomEdits((prev) => ({ ...prev, [residentId]: "" }));
      return moveResident(residentId, null);
    },
    [moveResident]
  );

  const handleSort = useCallback(
    (field) => {
      if (sortField === field) {
        setSortAsc((prev) => !prev);
        return;
      }
      setSortField(field);
      setSortAsc(true);
    },
    [sortField]
  );

  const sortedResidents = useMemo(() => {
    const rows = [...filteredResidents];
    if (!sortField) return rows;

    rows.sort((a, b) => {
      const roomA = formatHouseRoom(getAssignedRoom(displayState.rooms, a.id));
      const roomB = formatHouseRoom(getAssignedRoom(displayState.rooms, b.id));

      const values = {
        name: [
          `${a.name} ${a.surname}`.toLowerCase(),
          `${b.name} ${b.surname}`.toLowerCase(),
        ],
        room: [roomA.toLowerCase(), roomB.toLowerCase()],
        age: [Number(a.age) || 0, Number(b.age) || 0],
        campus: [
          String(a.campus || "").toLowerCase(),
          String(b.campus || "").toLowerCase(),
        ],
      };

      const [left, right] = values[sortField] || ["", ""];

      if (left < right) return sortAsc ? -1 : 1;
      if (left > right) return sortAsc ? 1 : -1;
      return 0;
    });

    return rows;
  }, [displayState.rooms, filteredResidents, sortAsc, sortField, formatHouseRoom]);

  const visibleBoardRooms = useMemo(() => {
    const search = String(roomNameSearch || "").trim().toLowerCase();

    return displayState.rooms.filter((room) => {
      const displayName = formatHouseRoom(room).toLowerCase();
      const roomHouse = String(room.house || "").trim();

      if (selectedHouse && roomHouse !== selectedHouse) return false;
      if (search && !displayName.includes(search)) return false;

      return true;
    });
  }, [displayState.rooms, roomNameSearch, selectedHouse, formatHouseRoom]);

  const visibleTableResidents = useMemo(() => {
    const search = String(roomNameSearch || "").trim().toLowerCase();

    return sortedResidents.filter((resident) => {
      const assignedRoom = getAssignedRoom(displayState.rooms, resident.id);
      const assignedRoomDisplay = formatHouseRoom(assignedRoom).toLowerCase();
      const assignedHouse = String(assignedRoom?.house || "").trim();
      const isAssigned = Boolean(assignedRoomDisplay);

      const matchesRoomSearch =
        !search || assignedRoomDisplay.includes(search);

      const matchesMode =
        roomFilterMode === "all" ||
        (roomFilterMode === "assigned" && isAssigned) ||
        (roomFilterMode === "unassigned" && !isAssigned);

      const matchesHouse =
        !selectedHouse || (isAssigned && assignedHouse === selectedHouse);

      return matchesRoomSearch && matchesMode && matchesHouse;
    });
  }, [
    displayState.rooms,
    roomFilterMode,
    roomNameSearch,
    selectedHouse,
    sortedResidents,
    formatHouseRoom,
  ]);

  const handleBulkUpdateRooms = useCallback(async (rooms) => {
    try {
      await bulkUpdateBedrooms(rooms);
      showToast("Rooms updated successfully", "success");
      await fetchResidents();
      await fetchRooms();
      await fetchManagementRooms();
    } catch (error) {
      console.error(error);
      showToast(error.message || "Failed to update rooms", "error");
      throw error;
    }
  }, [fetchResidents, fetchRooms, fetchManagementRooms, showToast]);

  const handleSaveRoomSchedule = useCallback(async (roomId, year, dates) => {
    try {
      await saveRoomSchedule(roomId, year, dates);
      showToast("Room schedule updated successfully", "success");
      await fetchManagementRooms();
    } catch (error) {
      console.error(error);
      showToast(error.message || "Failed to update room schedule", "error");
      throw error;
    }
  }, [fetchManagementRooms, showToast]);

  return {
    selectedDate,
    formattedDate,
    selectedCampus,
    selectedHouse,
    selectedGender,
    selectedAge,
    selectedNationality,
    nameSearch,
    roomNameSearch,
    roomFilterMode,
    sortField,
    sortAsc,
    loading,
    isSaving,
    toast,
    addRoomDialogOpen,
    residents,
    filteredResidents,
    visibleTableResidents,
    visibleBoardRooms,
    managementRooms,
    displayState,
    databaseState,
    campusOptions,
    houses,
    genders,
    ages,
    nationalities,
    availableRoomNames,
    validationErrors,
    pendingChanges,
    hasUnsavedChanges,
    placementWarnings,
    coverageGaps,
    dismissResidentWarning,
    undoResidentWarning,
    clearAllWarnings,
    wholeStayPrompt,
    confirmWholeStay,
    keepFromViewedDate,
    cancelWholeStayMove,
    capacityConflict,
    confirmCapacityClamp,
    revertCapacityConflict,
    dismissCapacityConflict,
    showToast,
    closeToast,
    setSelectedCampus,
    setSelectedHouse,
    setSelectedGender,
    setSelectedAge,
    setSelectedNationality,
    setNameSearch,
    setRoomNameSearch,
    setRoomFilterMode,
    setAddRoomDialogOpen,
    setDateWithAutoSave,
    changeDate,
    goToDate,
    shiftDate,
    moveResident,
    assignResidentByRoomName,
    clearRoomAssignment,
    saveChanges: persistChanges,
    downloadFireList,
    downloadRoomPdf,
    downloadRoomSigns,
    handleAddRoom,
    handleUpdateRoom,
    handleBulkUpdateRooms,
    handleDeleteRoom,
    getAssignedRoomName: (residentId) =>
      getAssignedRoomName(displayState.rooms, residentId),
    getAssignedRoomHouse: (residentId) =>
      getAssignedRoomHouse(displayState.rooms, residentId),
    getAssignedRoomEnsuite: (residentId) =>
      getAssignedRoomEnsuite(displayState.rooms, residentId),
    getDraftRoomName,
    getCurrentRoomForResident,
    handleSort,
    handleSaveRoomSchedule,
  };
}